import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Check, ChevronsUpDown, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UserAvatar } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export interface SelectableUser {
  id: string;
  name: string;
  email?: string;
}

export interface UserSelectProps {
  users: readonly SelectableUser[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  /** Adds a "Sin asignar" option that clears the field. */
  allowUnassigned?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

const UNASSIGNED = '__none__';

/** Owner picker: search by name or email, arrow keys to move, Enter to pick. */
export function UserSelect({
  users,
  value,
  onChange,
  placeholder = 'Sin asignar',
  allowUnassigned = true,
  disabled = false,
  id,
  className,
  'aria-label': ariaLabel,
}: UserSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = users.find((user) => user.id === value) ?? null;

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? users.filter(
          (user) =>
            user.name.toLowerCase().includes(needle) ||
            (user.email ?? '').toLowerCase().includes(needle),
        )
      : users;
    const options: Array<SelectableUser | { id: typeof UNASSIGNED; name: string }> = [...matched];
    if (allowUnassigned && !needle) options.unshift({ id: UNASSIGNED, name: placeholder });
    return options;
  }, [users, query, allowUnassigned, placeholder]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const commit = (optionId: string) => {
    onChange(optionId === UNASSIGNED ? null : optionId);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        if (results.length === 0) return 0;
        return (current + delta + results.length) % results.length;
      });
      return;
    }
    if (event.key === 'Enter') {
      const option = results[activeIndex];
      if (option) {
        event.preventDefault();
        commit(option.id);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          disabled={disabled}
          className={cn('w-full justify-between gap-2 font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected ? (
              <>
                <UserAvatar name={selected.name} size="xs" />
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              <>
                <UserRound className="size-3.5 text-muted-foreground" aria-hidden />
                <span className="truncate text-muted-foreground">{placeholder}</span>
              </>
            )}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-64 p-0" onKeyDown={onKeyDown}>
        <div className="border-b border-border p-1.5">
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar persona…"
            aria-label="Buscar persona"
            aria-controls="user-select-list"
            className="h-7"
          />
        </div>

        <div
          id="user-select-list"
          ref={listRef}
          role="listbox"
          aria-label="Personas"
          className="max-h-64 overflow-y-auto scrollbar-thin p-1"
        >
          {results.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              Nadie coincide con la búsqueda
            </p>
          )}
          {results.map((option, index) => {
            const isSelected = option.id === UNASSIGNED ? value === null : option.id === value;
            const user = option.id === UNASSIGNED ? null : (option as SelectableUser);
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-active={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => commit(option.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                  index === activeIndex && 'bg-muted',
                )}
              >
                {user ? (
                  <UserAvatar name={user.name} size="xs" />
                ) : (
                  <UserRound className="size-4 text-muted-foreground" aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.name}</span>
                  {user?.email && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  )}
                </span>
                {isSelected && <Check className="size-3.5 shrink-0" aria-hidden />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
